import axios, { AxiosRequestConfig, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { message } from 'antd';
import { getToken, removeToken, setToken } from './auth';
import { logError } from './logger';
import { getErrorMessage, getStatusMessage } from './errorHandler';

// Flag to prevent multiple refresh requests
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
  config: AxiosRequestConfig;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      if (token && prom.config.headers) {
        prom.config.headers.Authorization = `Bearer ${token}`;
      }
      prom.resolve(request(prom.config));
    }
  });
  
  failedQueue = [];
};

const refreshAccessToken = async (refreshToken: string, originalConfig: AxiosRequestConfig) => {
  if (isRefreshing) {
    // If already refreshing, queue this request
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject, config: originalConfig });
    });
  }

  isRefreshing = true;

  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1'}/auth/refresh`,
      { refresh_token: refreshToken }
    );

    const { access_token } = response.data;
    setToken(access_token);
    
    // Update the original request with new token
    if (originalConfig.headers) {
      originalConfig.headers.Authorization = `Bearer ${access_token}`;
    }

    processQueue(null, access_token);
    isRefreshing = false;

    // Retry the original request
    return request(originalConfig);
  } catch (error) {
    processQueue(error, null);
    isRefreshing = false;
    
    // Refresh failed, redirect to login
    removeToken();
    message.error('登录已过期，请重新登录');
    setTimeout(() => {
      window.location.href = '/login';
    }, 1000);
    
    return Promise.reject(error);
  }
};

// Create axios instance
const request = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: {
    indexes: null, // 使用 param=value1&param=value2 格式而不是 param[]=value
  },
});

// Request interceptor
request.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add token to request headers
    const token = getToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    logError('Request interceptor error', error);
    return Promise.reject(error);
  }
);

// Response interceptor
request.interceptors.response.use(
  (response: AxiosResponse) => {
    const { data } = response;

    // Check if response indicates success - handle both backend response formats
    if (data && data.success !== undefined && !data.success) {
      message.error(data.message || data.detail || '请求失败');
      return Promise.reject(new Error(data.message || data.detail || '请求失败'));
    }

    return data;
  },
  (error: AxiosError) => {
    logError('Response interceptor error', error);

    // Handle different error status codes
    if (error.response) {
      const { status, data } = error.response;

      // Special handling for 401 - try to refresh token
      if (status === 401) {
        // Try to refresh token
        const refreshToken = localStorage.getItem('law_firm_crm_refresh_token');
        if (refreshToken && !error.config?.url?.includes('/auth/refresh')) {
          // Avoid infinite loop
          return refreshAccessToken(refreshToken, error.config!);
        }
        
        // If no refresh token or refresh failed, redirect to login
        message.error('登录已过期，请重新登录');
        removeToken();
        setTimeout(() => {
          window.location.href = '/login';
        }, 1000);
        return Promise.reject(error);
      }

      // Special handling for 404 with deleted records
      if (status === 404) {
        const errorMessage404 = (data as any)?.detail || (data as any)?.message;
        if (errorMessage404 && errorMessage404.includes('deleted')) {
          message.error('该记录已被删除，请刷新页面');
          return Promise.reject(error);
        }
      }

      // Special handling for outbound follow-up requirement (400 with lead_id in detail)
      // Don't show automatic error message, let the component handle it
      if (status === 400 && (data as any)?.detail?.lead_id) {
        return Promise.reject(error);
      }

      // Use unified error message handler
      const errorMessage = getErrorMessage(error);
      message.error(errorMessage);
    } else if (error.request) {
      message.error('网络连接失败，请检查网络设置');
    } else {
      message.error('请求配置错误');
    }

    return Promise.reject(error);
  }
);

// Generic request function with proper typing
export const apiRequest = async <T = any>(
  config: AxiosRequestConfig
): Promise<T> => {
  try {
    const response = await request(config);
    return response as T;
  } catch (error) {
    throw error;
  }
};

// Convenience methods
export const get = <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> =>
  apiRequest<T>({ ...config, method: 'GET', url });

export const post = <T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<T> =>
  apiRequest<T>({ ...config, method: 'POST', url, data });

export const put = <T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<T> =>
  apiRequest<T>({ ...config, method: 'PUT', url, data });

export const patch = <T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<T> =>
  apiRequest<T>({ ...config, method: 'PATCH', url, data });

export const del = <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> =>
  apiRequest<T>({ ...config, method: 'DELETE', url });

export default request;